import { useAsyncData } from '#imports'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import { useFetchCMSPageContentWithFallback } from './useFetchCMSPageContentWithFallback'
import { useFetchFormattedPageContent } from './useFetchFormattedPageContent'
import { usePageParamsFromRoute } from './usePageParamsFromRoute'
import { useSetCMSPageCacheTagHeader } from './useSetCMSPageCacheTagHeader'

export async function useFetchCMSPageContent({
  fetchFallbackOnError,
  experienceLayerIsEnabled,
}: {
  fetchFallbackOnError: boolean
  experienceLayerIsEnabled?: boolean
}) {
  const pathParams = usePageParamsFromRoute()

  const { setCMSCacheTagHeader } = useSetCMSPageCacheTagHeader()

  const hasPageCategory = !isEmpty(pathParams.pageCategory)

  const endpoint = hasPageCategory
    ? '/content/page/:pageType/:pageName/:pageCategory'
    : '/content/page/:pageType/:pageName'

  if (!experienceLayerIsEnabled) {
    const response = fetchFallbackOnError
      ? await useFetchCMSPageContentWithFallback(endpoint, {
          pathParams,
        })
      : await useFetchFormattedPageContent(endpoint, {
          pathParams,
        })

    setCMSCacheTagHeader(pathParams)

    return response
  }

  if (hasPageCategory) {
    const response = await useFetchCMSPageContentCategoryWithXPLayer(pathParams)

    setCMSCacheTagHeader(pathParams)

    return response
  }

  const response = await useFetchCMSPageContentWithXPLayer(pathParams)

  setCMSCacheTagHeader(pathParams)

  return response
}

function useFetchCMSPageContentWithXPLayer(
  pathParams: Record<'pageType' | 'pageName', string>,
) {
  const $httpFetch = useHttpFetch()
  const key = getAsyncDataKey('cms-formatted-page-content', pathParams)

  return useAsyncData(key, async () =>
    $httpFetch('/front-office/content/page/:pageType/:pageName', {
      pathParams,
      baseURL: '',
    }),
  )
}

function useFetchCMSPageContentCategoryWithXPLayer(
  pathParams: Record<'pageType' | 'pageName' | 'pageCategory', string>,
) {
  const $httpFetch = useHttpFetch()
  const key = getAsyncDataKey('cms-formatted-page-content', pathParams)

  return useAsyncData(key, () =>
    $httpFetch('/front-office/content/page/:pageType/:pageName/:pageCategory', {
      pathParams,
      baseURL: '',
    }),
  )
}
